import { useMutation } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import {
    BookingsService,
    type ComputePackagePriceBookingsReqDto,
    type ComputePackagePriceBookingsResDto,
} from '../../generated';
import type { CustomMutationOptions } from '../common.types';

export const useComputePackagePrice = (
    params?: ComputePackagePriceBookingsReqDto,
    options?: CustomMutationOptions<
        ComputePackagePriceBookingsReqDto,
        ComputePackagePriceBookingsResDto
    >
) => {
    const [state, setState] = useState<{
        data?: ComputePackagePriceBookingsResDto;
        params?: ComputePackagePriceBookingsReqDto;
        isLoading: boolean;
    }>({
        data: undefined,
        params: undefined,
        isLoading: false,
    });

    const { mutate: computePrice, error } = useMutation({
        mutationFn: async body => {
            const response = await BookingsService.computePackagePriceBookings({
                body,
            });

            if (!response.data) {
                throw new Error('Could not compute package price.');
            }

            return response.data;
        },
        ...options,
        onSuccess: (data, nextParams, context) => {
            setState({
                data,
                params: nextParams,
                isLoading: false,
            });
            options?.onSuccess?.(data, nextParams, context);
        },
        onError: (mutationError, nextParams, context) => {
            setState(prevState => ({
                ...prevState,
                isLoading: false,
            }));
            options?.onError?.(mutationError, nextParams, context);
        },
    });

    const changed = useMemo(() => {
        const oldParams = state.params ? JSON.stringify(state.params) : '';
        const newParams = params ? JSON.stringify(params) : '';

        return oldParams !== newParams;
    }, [params, state.params]);

    const update = useCallback(() => {
        if (!params) return;

        setState(prevState => ({
            ...prevState,
            isLoading: true,
            params,
        }));
        computePrice(params);
    }, [computePrice, params]);

    return {
        data: state.data,
        isLoading: state.isLoading,
        changed,
        update,
        error,
    };
};
